// Phase 4 — Legacy broadcast-style flyer used for Volleyball (single +
// dual-team Summer Volleyball). When the match sport is Cricket this
// component delegates to CricketFlyerV3 so the routing stays in one
// place. Pure render; all data comes from props.

import React from "react";
import { CalendarDays, Clock, MapPin } from "lucide-react";
import {
  playerSkillCodes, teamLogoSrc,
  splitVenue, formatDateTemplate, formatTimeTemplate
} from "./flyerHelpers";
import {
  FlyerCard, FlyerRosterColumn
} from "./FlyerDual";
import { CricketFlyerV3 } from "./FlyerCricket";
import { SummerVolleyballFlyer } from "./FlyerSummerVolleyball";

export function PosterPreview({
  match, opponent, captainMsg, reportingTime, venue,
  startingPlayers, benchPlayers, rules, team,
  teamBStartingPlayers = [], teamBBenchPlayers = [],
  snacksVolunteer, alcoholVolunteer,
  captainPickId = null, wicketKeeperId = null,
  flyerTemplate = null
}) {
  const isCricket  = match?.sport === "Cricket";
  const dual       = !!rules.dualTeam;
  const showVolunteers = !!(snacksVolunteer || alcoholVolunteer);

  const { short: venueShort, address: venueAddress } = splitVenue(venue);
  const sportUpper = (match?.sport || "").toUpperCase();
  const opp = (opponent || match?.opponent || "OPPONENT").toUpperCase();
  // HOME team name on the flyer derived from the team that's playing — strip
  // sport / suffix words so "Rowdys Cricket Club" reads as "ROWDYS" and
  // "Pitt Gabruz Cricket Team" reads as "PITT GABRUZ".
  const homeTeamName = (team?.name || "Pitt Gabruz")
    .replace(/\b(Cricket|Volleyball|Team|Club)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase() || "PITT GABRUZ";

  // Cricket V3 layout is single-team only. Summer Volleyball uses its
  // own fancy dual layout. Anything else falls through to the legacy
  // broadcast layout below.
  const useCricketV2 = isCricket && !dual;
  const useSummerVB  = team?.code === "SUMMER_VOLLEYBALL";
  const logoSrc = teamLogoSrc(team);

  if (useSummerVB) {
    return (
      <SummerVolleyballFlyer
        match={match}
        reportingTime={reportingTime}
        venue={venue}
        startingPlayers={startingPlayers}
        benchPlayers={benchPlayers}
        teamBStartingPlayers={teamBStartingPlayers}
        teamBBenchPlayers={teamBBenchPlayers}
        rules={rules}
        snacksVolunteer={snacksVolunteer}
        alcoholVolunteer={alcoholVolunteer}
      />
    );
  }

  if (useCricketV2) {
    const footerBrand = team?.code === "ROWDYS_CRICKET" ? "ROWDYS" : "GABRUZ";
    const homeAcronym = (homeTeamName.match(/\b[A-Z]/g) || ["G"]).join("").slice(0, 2);
    return (
      <CricketFlyerV3
        match={match}
        opponent={opponent}
        opp={opp}
        homeTeamName={homeTeamName}
        homeAcronym={homeAcronym}
        footerBrand={footerBrand}
        team={team}
        rules={rules}
        startingPlayers={startingPlayers}
        reportingTime={reportingTime}
        venueShort={venueShort}
        venueAddress={venueAddress}
        captainMsg={captainMsg}
        showVolunteers={showVolunteers}
        snacksVolunteer={snacksVolunteer}
        alcoholVolunteer={alcoholVolunteer}
        captainPickId={captainPickId}
        wicketKeeperId={wicketKeeperId}
        template={flyerTemplate}
      />
    );
  }

  return (
    <div className={`poster poster--broadcast${dual ? " poster--dual" : ""}`}>
      <div className="flyerHead">
        <div className="flyerBrand">
          <img className="flyerBrand-mark" src={logoSrc} alt="Gabruz" crossOrigin="anonymous"
               onError={e => e.currentTarget.style.display = "none"} />
          <div className="flyerBrand-text">
            <h2>GABRUZ UNIVERSE</h2>
            {match?.sport !== "Volleyball" && (
              <small>PITTSBURGH {sportUpper} LEAGUE</small>
            )}
          </div>
        </div>
      </div>

      <div className="flyerHero">
        <div className="flyerTeam flyerTeam--home">
          <span className="flyerTeam-tag">HOME</span>
          <strong className="flyerTeam-name">{homeTeamName}</strong>
          <small className="flyerTeam-meta">{sportUpper}</small>
        </div>
        <div className="flyerVs">
          <span>VS</span>
        </div>
        <div className="flyerTeam flyerTeam--away">
          <span className="flyerTeam-tag">AWAY</span>
          <strong className="flyerTeam-name">{opp}</strong>
          <small className="flyerTeam-meta">{(match?.type || "League Match").toUpperCase()}</small>
        </div>
      </div>

      <div className="flyerStrip">
        <div className="flyerStrip-item">
          <div className="flyerStrip-icon"><CalendarDays size={18} /></div>
          <div className="flyerStrip-text">
            <span>DATE</span>
            <b>{formatDateTemplate(match?.date)}</b>
          </div>
        </div>
        <div className="flyerStrip-item">
          <div className="flyerStrip-icon"><Clock size={18} /></div>
          <div className="flyerStrip-text">
            <span>REPORTING</span>
            <b>{formatTimeTemplate(reportingTime)}</b>
          </div>
        </div>
        <div className="flyerStrip-item">
          <div className="flyerStrip-icon"><MapPin size={18} /></div>
          <div className="flyerStrip-text">
            <span>GROUND</span>
            <b>{venueShort}</b>
            {venueAddress && <em>{venueAddress}</em>}
          </div>
        </div>
      </div>

      {dual ? (
        <div className="flyerDual">
          <FlyerRosterColumn
            heading="PITT GABRUZ"
            players={startingPlayers}
            bench={benchPlayers}
            benchLabel={`BACKUPS (${benchPlayers.length})`}
            team={team}
            capacity={rules.startingSize}
          />
          <FlyerRosterColumn
            heading="PITT BAAZIGARS"
            players={teamBStartingPlayers}
            bench={teamBBenchPlayers}
            benchLabel={`BACKUPS (${teamBBenchPlayers.length})`}
            team={team}
            capacity={rules.startingSize}
          />
        </div>
      ) : (
        <div className="flyerRoster">
          <div className="flyerRoster-head">
            <h3>{rules.playingLabel}</h3>
            <span>{startingPlayers.length}/{rules.startingSize}</span>
          </div>
          <div className="flyerCards">
            {Array.from({ length: rules.startingSize }).map((_, i) => {
              const p = startingPlayers[i];
              const codes = p ? playerSkillCodes(team, p.id) : [];
              return <FlyerCard key={i} index={i} player={p} codes={codes} />;
            })}
          </div>
          {!isCricket && benchPlayers.length > 0 && (
            <div className="flyerBench">
              <span className="flyerBench-tag">BACKUPS</span>
              <div className="flyerBench-list">
                {benchPlayers.map((p, i) => (
                  <span key={p.id} className="flyerBench-chip">{i + 1}. {p.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cricket reframes the two volunteer slots as hydration / fuel
          (Water/Gatorade + Bananas/Protein Bars) instead of Snacks /
          Alcohol. The underlying snacks/alcohol storage keys are
          unchanged so existing saved squads still round-trip. */}
      {showVolunteers && (
        <div className="flyerVolunteers">
          <span className="flyerVolunteers-tag">VOLUNTEERS</span>
          <div className="flyerVolunteers-row">
            <div>
              <em>{isCricket ? "WATER / GATORADE" : "SNACKS"}</em>
              <b>{snacksVolunteer || "—"}</b>
            </div>
            <div>
              <em>{isCricket ? "BANANAS / PROTEIN BARS" : "ALCOHOL"}</em>
              <b>{alcoholVolunteer || "—"}</b>
            </div>
          </div>
        </div>
      )}

      {/* Rowdys is its own sub-club inside the Gabruz Universe — their
          flyer signs off with ROWDYS instead of the parent GABRUZ brand. */}
      <div className="flyerFoot">
        <span>ONE TEAM</span>
        <span>·</span>
        <span>ONE VISION</span>
        <span>·</span>
        <span className="flyerFoot-brand">
          {team?.code === "ROWDYS_CRICKET" ? "ROWDYS" : "GABRUZ"}
        </span>
      </div>
    </div>
  );
}
